import React, { useState } from "react";
import { Tooltip } from "antd";
import CustomModal from "../../components/common/CustomModal";
import { FaEye, FaBuilding, FaUser } from "react-icons/fa";
import {
  FiMapPin,
  FiCalendar,
  FiDownload,
  FiLoader,
  FiHelpCircle,
} from "react-icons/fi";
import { GrTransaction } from "react-icons/gr";
import Table from "../../components/Table";
import SelectDropdown from "../../components/common/SelectDropdown";
import Layout from "../../components/global/layout";
import shared from "./shared";

function Html({
  sorting,
  filter,
  view,
  pageChange,
  count,
  clear,
  filters,
  setFilter,
  total,
  loaging,
  data,
  sortClass,
  venues,
  users,
  changeVenue,
  changeUser,
  changeType,
  selectedTransaction,
  isModalVisible,
  closeModal,
  handleExport,
  setIsExporting,
  isExporting,
}) {
  const [isHelpModalVisible, setIsHelpModalVisible] = useState(false);
  const getStatusBadge = (status) => {
    switch (status) {
      case "success":
        return "bg-[#E5F9E6] text-[#00BF06] border-[#ADE6AF]";
      case "pending":
        return "bg-[#FFF8E1] text-[#CC9900] border-[#FFE082]";
      case "failed":
        return "bg-[#ffefef] text-[#ff3b3b] border-[#ffa6a6]";
      default:
        return "bg-gray-100 text-gray-500 border-gray-200";
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case "earned":
      case "credit":
        return "bg-[#E5F9E6] text-[#059669] border-[#ADE6AF]";
      case "redeemed":
      case "debit":
        return "bg-[#FFF3E0] text-[#ea580c] border-[#FFE0B2]";
      default:
        return "bg-gray-100 text-gray-500 border-gray-200";
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "earned":
      case "credit":
        return "Earned";
      case "redeemed":
      case "debit":
        return "Redeemed";
      default:
        return type || "--";
    }
  };

  const getPointsDisplay = (row) => {
    const points = row?.creditPoints || row?.redeemPoints || row?.points || 0;
    const type = row?.type;
    const isEarned = type === "earned" || type === "credit";
    return {
      value: `${isEarned ? "+" : "-"}${points}`,
      color: isEarned ? "text-[#059669]" : "text-[#ea580c]",
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return "--";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns = [
    {
      key: "userEmail",
      name: "User",
      render: (row) => (
        <div className="flex flex-col">
          <span
            className="font-semibold text-gray-800 text-sm capitalize whitespace-normal line-clamp-2
"
          >
            {row?.userDetail?.fullName ||
              row?.userFullName ||
              row?.fullName ||
              "--"}
          </span>
          <span className="text-xs text-gray-400 truncate max-w-[200px]">
            {row?.userDetail?.email || row?.userEmail || "--"}
          </span>
        </div>
      ),
    },
    {
      key: "venue",
      name: "Venue",
      render: (row) => (
        <span className="text-sm text-gray-600 font-medium capitalize">
          {row?.venue?.name ||
            row?.retailerCompanyName ||
            row?.venueName ||
            "N/A"}
        </span>
      ),
    },
    {
      key: "type",
      name: "Type",
      render: (row) => (
        <span
          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium capitalize border ${getTypeBadge(
            row.type,
          )}`}
        >
          {getTypeLabel(row.type)}
        </span>
      ),
    },
    {
      key: "points",
      name: "Points",
      render: (row) => {
        const pts = getPointsDisplay(row);
        return (
          <span className={`text-sm font-bold ${pts.color}`}>{pts.value}</span>
        );
      },
    },
    {
      key: "status",
      name: "Status",
      render: (row) => (
        <span
          className={`w-fit text-xs px-3 py-1 flex items-center justify-center border rounded-full capitalize font-medium ${getStatusBadge(
            row.status,
          )}`}
        >
          {row.status || "--"}
        </span>
      ),
    },
    {
      key: "createdAt",
      name: "Date",
      render: (row) => (
        <span className="text-sm text-gray-600 whitespace-nowrap">
          {formatDate(row?.createdAt || row?.updatedAt)}
        </span>
      ),
    },
    {
      key: "notes",
      name: "Notes",
      render: (row) => (
        <span className="text-sm text-gray-500 truncate max-w-[180px] block">
          {row?.notes || row?.discription || "--"}
        </span>
      ),
    },
    {
      key: "action",
      name: "Action",
      render: (itm) => (
        <div className="flex items-center justify-start gap-1.5">
          <Tooltip placement="top" title="View Details">
            <a
              onClick={() => view(itm?._id || itm?.id)}
              className="cursor-pointer rounded-full w-8 h-8 flex items-center justify-center text-sm hover:opacity-80 bg-[#E5EFFD] text-[#005AEF]"
            >
              <FaEye />
            </a>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <Layout>
      <div className="flex flex-wrap justify-between items-center gap-y-4 mb-6">
        <div>
          <h3 className="text-2xl font-bold text-[#111827]">
            Transaction History
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            View all transaction records across venues and users
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={isExporting}
          className={`bg-[#CC9900] h-10 flex justify-center items-center shadow-md px-6 hover:opacity-90 text-sm text-white rounded-full gap-2 cursor-pointer font-semibold transition-all ${isExporting ? "opacity-70 cursor-not-allowed" : "active:scale-95"}`}
        >
          {isExporting ? (
            <>
              <FiLoader className="text-lg animate-spin" />
              Exporting...
            </>
          ) : (
            <>
              <FiDownload className="text-lg" />
              Export CSV
            </>
          )}
        </button>
      </div>

      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex items-center flex-wrap gap-3 mb-6 w-full justify-between">
          {/* Search */}
          <form
            className="relative flex-1 max-sm:min-w-full min-w-[240px] max-w-sm"
            onSubmit={(e) => {
              e.preventDefault();
              filter();
            }}
          >
            <input
              type="text"
              value={filters.search}
              onChange={(e) => {
                setFilter({ ...filters, search: e.target.value });
                if (e.target.value === "") filter({ search: "" });
              }}
              className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-full focus:ring-[#CC9900] focus:border-[#CC9900] block w-full h-10 px-5 pr-11 transition-all outline-none"
              placeholder="Search transactions..."
            />
            <button
              type="submit"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#CC9900]"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m21 21-4.35-4.35M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z"
                />
              </svg>
            </button>
          </form>

          {/* Filters */}
          <div className="max-sm:w-full flex gap-2 flex-wrap">
            {/* Venue Filter */}
            <div className="max-sm:min-w-full min-w-[200px]">
              <SelectDropdown
                id="venueId"
                displayValue="name"
                placeholder="All Venues"
                theme="search"
                isClearable={true}
                intialValue={filters.venueId}
                result={(e) => {
                  changeVenue(e.value);
                }}
                options={venues.map((v) => ({
                  id: v._id || v.id,
                  name: v.name,
                  value: v._id || v.id,
                }))}
              />
            </div>

            {/* User Filter */}
            <div className="max-sm:min-w-full min-w-[200px]">
              <SelectDropdown
                id="userId"
                displayValue="name"
                placeholder="All Users"
                theme="search"
                isClearable={true}
                intialValue={filters.userId}
                // onInputChange={(value) => {
                //     getUsers({
                //         search: value,
                //         venueId: filters.venueId,
                //         status: 'success'
                //     })
                // }}

                result={(e) => {
                  changeUser(e.value);
                }}
                options={users}
              />
            </div>

            {/* Type Filter */}
            <div className="max-sm:min-w-full min-w-[160px]">
              <SelectDropdown
                id="type"
                displayValue="name"
                placeholder="All Types"
                theme="search"
                isClearable={true}
                intialValue={filters.type}
                result={(e) => {
                  changeType(e.value);
                }}
                options={[
                  { id: "earned", name: "Earned", value: "earned" },
                  { id: "redeemed", name: "Redeemed", value: "redeemed" },
                ]}
              />
            </div>

            {/* Reset */}
            {(filters.search ||
              filters.type ||
              filters.venueId ||
              filters.userId) && (
              <button
                onClick={() => clear()}
                className="bg-gray-100 hover:bg-gray-200 h-10 px-6 rounded-full text-sm font-semibold transition-colors text-gray-600"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {!loaging ? (
          <Table
            className="transaction-table"
            data={data}
            columns={columns}
            page={filters.page}
            count={filters.count}
            filters={filters}
            total={total}
            result={(e) => {
              if (e.event === "page") pageChange(e.value);
              if (e.event === "sort") {
                sorting(e.value);
                sortClass(e.value);
              }
              if (e.event === "count") count(e.value);
            }}
          />
        ) : (
          <div className="py-20 text-center text-gray-400">
            Loading transactions...
          </div>
        )}
      </div>

      {/* Transaction Detail Modal */}
      <CustomModal open={isModalVisible} onClose={closeModal} width={700}>
        {selectedTransaction && (
          <div className="flex flex-col max-h-[calc(100vh_-_70px)]">
            {/* Modal Header */}
            <div className="relative flex items-center justify-between gap-4 border-b border-gray-200 px-5 sm:px-6 py-4 sm:py-5">
              {/* Left Content */}
              <div className="min-w-0 flex-1">
                <h3 className="flex items-center gap-2 text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                  <GrTransaction className="text-[#CC9900] shrink-0 text-xl sm:text-2xl" />
                  <span className="truncate">Transaction Details</span>
                </h3>

                <p className="text-xs sm:text-sm text-gray-500 mt-1.5 truncate">
                  ID: {selectedTransaction._id || selectedTransaction.id}
                </p>
              </div>

              {/* Status */}
              <div className="shrink-0 mr-8 sm:mr-10">
                <div
                  className={`px-3 sm:px-4 py-1.5 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-wider border whitespace-nowrap ${getStatusBadge(
                    selectedTransaction.status,
                  )}`}
                >
                  {selectedTransaction.status}
                </div>
              </div>
            </div>

            <div className="overflow-auto scrollbar p-5">
              <div className="grid grid-cols-1 gap-5">
                {/* Type & Points */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#CC990008] border border-[#CC990015] p-3 rounded-xl">
                    <h4 className="text-[10px] font-bold text-[#CC9900] uppercase mb-1">
                      Transaction Type
                    </h4>
                    <p className="text-sm font-bold text-gray-800 capitalize">
                      {getTypeLabel(selectedTransaction.type)}
                    </p>
                  </div>
                  <div className="bg-[#CC990008] border border-[#CC990015] p-3 rounded-xl">
                    <h4 className="text-[10px] font-bold text-[#CC9900] uppercase mb-1">
                      Points
                    </h4>
                    <p
                      className={`text-lg font-black ${
                        getPointsDisplay(selectedTransaction).color
                      }`}
                    >
                      {getPointsDisplay(selectedTransaction).value}
                    </p>
                  </div>
                </div>

                {/* User & Venue Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                    <h4 className="text-[10px] font-bold text-gray-400 uppercase mb-3 tracking-widest">
                      User Details
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-sm">
                        <FaUser className="text-[#CC9900] text-xs" />
                        <div className="flex flex-col">
                          <span className="text-[10px] text-gray-400 uppercase">
                            Name
                          </span>
                          <span className="text-gray-800 font-medium capitalize">
                            {selectedTransaction?.userDetail?.fullName ||
                              selectedTransaction?.userFullName ||
                              selectedTransaction?.fullName ||
                              "--"}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <svg
                          className="w-3 h-3 text-[#CC9900]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        <div className="flex flex-col">
                          <span className="text-[10px] text-gray-400 uppercase">
                            Email
                          </span>
                          <span className="text-gray-800 font-medium break-all">
                            {selectedTransaction?.userDetail?.email ||
                              selectedTransaction?.userEmail ||
                              "--"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                    <h4 className="text-[10px] font-bold text-gray-400 uppercase mb-3 tracking-widest">
                      Venue & Location
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-sm">
                        <FaBuilding className="text-[#CC9900] text-xs" />
                        <div className="flex flex-col">
                          <span className="text-[10px] text-gray-400 uppercase">
                            Venue
                          </span>
                          <span className="text-gray-800 font-medium capitalize">
                            {selectedTransaction?.venue?.name ||
                              selectedTransaction?.retailerCompanyName ||
                              selectedTransaction?.venueName ||
                              "N/A"}
                          </span>
                        </div>
                      </div>
                      {(selectedTransaction?.locations?.city ||
                        selectedTransaction?.locations?.state) && (
                        <div className="flex items-center gap-3 text-sm">
                          <FiMapPin className="text-[#CC9900] text-xs" />
                          <div className="flex flex-col">
                            <span className="text-[10px] text-gray-400 uppercase">
                              Location
                            </span>
                            <span className="text-gray-800 font-medium">
                              {[
                                selectedTransaction?.locations?.city,
                                selectedTransaction?.locations?.state,
                                selectedTransaction?.locations?.country,
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Date & Notes */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                    <h4 className="text-[10px] font-bold text-gray-400 uppercase mb-3 tracking-widest">
                      Timestamp
                    </h4>
                    <div className="flex items-center gap-3 text-sm">
                      <FiCalendar className="text-[#CC9900]" />
                      <div className="flex flex-col">
                        <span className="text-[10px] text-gray-400 uppercase">
                          Created At
                        </span>
                        <span className="text-gray-800 font-medium">
                          {formatDate(
                            selectedTransaction?.createdAt ||
                              selectedTransaction?.updatedAt,
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {(selectedTransaction?.notes ||
                    selectedTransaction?.discription) && (
                    <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                      <h4 className="text-[10px] font-bold text-gray-400 uppercase mb-3 tracking-widest">
                        Notes
                      </h4>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {selectedTransaction?.notes ||
                          selectedTransaction?.discription ||
                          "--"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            {/* */}
          </div>
        )}
      </CustomModal>

      {/* How It Works Guide Modal */}
      <CustomModal
        open={isHelpModalVisible}
        onClose={() => setIsHelpModalVisible(false)}
        width={650}
      >
        <div className="flex flex-col font-sans">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#CC9900] via-[#E6B800] to-[#CC9900] p-6 text-white relative">
            <div className="flex justify-between items-start gap-4">
              <div>
                <h3 className="text-xl font-bold leading-snug">
                  💡 How Transaction Tracking Works
                </h3>
                <p className="text-sm text-white/90 mt-1">
                  Understanding multi-location ledger and points activity
                </p>
              </div>
            </div>
          </div>

          {/* Ticket Dotted Separator */}
          <div className="relative bg-gray-50" style={{ height: "28px" }}>
            <svg
              className="absolute left-0 top-0 h-full"
              style={{ width: "14px" }}
              viewBox="0 0 14 28"
              preserveAspectRatio="none"
            >
              <path d="M0,0 C14,0 14,28 0,28 Z" fill="white" />
            </svg>
            <div
              className="absolute"
              style={{
                top: "50%",
                left: "14px",
                right: "14px",
                transform: "translateY(-50%)",
                borderTop: "1.5px dashed #d1d5db",
              }}
            />
            <svg
              className="absolute right-0 top-0 h-full"
              style={{ width: "14px" }}
              viewBox="0 0 14 28"
              preserveAspectRatio="none"
            >
              <path d="M14,0 C0,0 0,28 14,28 Z" fill="white" />
            </svg>
          </div>

          {/* Modal Body */}
          <div className="p-6 space-y-6">
            <p className="text-sm text-gray-600 leading-relaxed">
              This ledger displays all point transaction history across
              different venues, customer accounts, stations, and locations.
            </p>

            <div className="space-y-4">
              {/* Stations & Locations */}
              <div className="flex gap-4 p-4 rounded-xl border border-[#CC9900]/20 bg-[#CC9900]/5">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#CC9900]/10 text-[#CC9900] flex-shrink-0">
                  <FiMapPin size={18} />
                </span>
                <div>
                  <h4 className="font-bold text-[#8B6E1E] text-sm mb-1">
                    Multi-Location & Station Records
                  </h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Transactions occur at various physical locations and
                    terminal stations. This dashboard aggregates all records
                    centrally, displaying the specific venue associated with
                    each transaction.
                  </p>
                </div>
              </div>

              {/* Earned vs Redeemed */}
              <div className="flex gap-4 p-4 rounded-xl border border-gray-200 bg-gray-50">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-200/50 text-gray-500 flex-shrink-0">
                  <GrTransaction size={16} />
                </span>
                <div>
                  <h4 className="font-bold text-gray-700 text-sm mb-1">
                    Earned vs. Redeemed Points
                  </h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    * **Earned (Credit)** points are added when customers
                    purchase or perform specific actions (indicated in green). *
                    **Redeemed (Debit)** points represent points spent to redeem
                    offers or vouchers (indicated in orange).
                  </p>
                </div>
              </div>

              {/* Export */}
              <div className="flex gap-4 p-4 rounded-xl border border-amber-100 bg-amber-50/50">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 text-amber-600 font-bold text-sm flex-shrink-0">
                  ⚙️
                </span>
                <div>
                  <h4 className="font-bold text-[#8B6E1E] text-sm mb-1">
                    Filters & Exporting
                  </h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Use the filters to drill down into transactions for specific
                    venues or users. Click **Export CSV** to download files for
                    external accounting and audits.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex justify-end">
            <button
              onClick={() => setIsHelpModalVisible(false)}
              className="px-5 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 transition-all text-xs font-semibold"
            >
              Got it, thanks!
            </button>
          </div>
        </div>
      </CustomModal>

      {/* Floating Help Button */}
      <Tooltip placement="left" title="How It Works">
        <button
          type="button"
          onClick={() => setIsHelpModalVisible(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-12 h-12 rounded-full bg-[#CC9900] text-white shadow-lg hover:bg-[#B38000] hover:scale-110 active:scale-95 transition-all duration-300"
        >
          <FiHelpCircle className="text-2xl" />
        </button>
      </Tooltip>
    </Layout>
  );
}

export default Html;
